import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { i18nRoot, maxChapterCount } from "../constants";
import type { MediaChapterFormData } from "../types";
import { formatTime } from "../utils/timeHelpers";

interface AddChapterButtonProps {
  chapters: MediaChapterFormData[];
  className?: string;
  currentTime: number;
  onChapterAdd: () => void;
}

export const AddChapterButton = ({
  className,
  chapters,
  currentTime,
  onChapterAdd,
}: AddChapterButtonProps) => {
  const chapterCount = chapters.length;
  const isChapterLimitReached = chapterCount >= maxChapterCount;
  const newChapterStartTime = chapterCount === 0 ? 0.0 : currentTime;

  const isDuplicateTimestamp = useMemo(
    () =>
      chapters.some(
        chapter =>
          Math.floor(chapter.start_time) === Math.floor(newChapterStartTime),
      ),
    [chapters, newChapterStartTime],
  );

  const addButtonTooltipText = useMemo(() => {
    if (isChapterLimitReached) {
      return t(`${i18nRoot}.chapters.chapter_limit_reached_tooltip`, {
        max_chapters_count: maxChapterCount,
      });
    }
    if (isDuplicateTimestamp) {
      return t(`${i18nRoot}.chapters.duplicate_timestamp_tooltip`, {
        timestamp: formatTime(currentTime),
      });
    }
    return "";
  }, [isChapterLimitReached, isDuplicateTimestamp, currentTime]);

  const isDisabled = isChapterLimitReached || isDuplicateTimestamp;

  return (
    <TippyV2 content={addButtonTooltipText} enabled={isDisabled}>
      <Button
        className={classNames("flex gap-1", className)}
        type="button"
        variant="secondary"
        onClick={onChapterAdd}
        disabled={isDisabled}
      >
        <Icon type="plus-v2" size={16} className="!size-4" />
        <Typography.LabelXs weight="medium">
          {t(`${i18nRoot}.chapters.add_at`, {
            timestamp: formatTime(currentTime),
          })}
        </Typography.LabelXs>
      </Button>
    </TippyV2>
  );
};
